/**
 * Home page - main landing page with authentication-aware content
 */

import { useNavigate } from 'react-router-dom';
import { useCompany } from '@/contexts/CompanyContext';
import {
  HeartIcon,
  CreditCardIcon,
  HandRaisedIcon,
  UserPlusIcon,
  ShieldCheckIcon,
  ClockIcon,
  BellIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '@/contexts/AuthContext';
import { DonorDashboard } from './DonorDashboard';
import { ImpactMetricsCard } from '@/components/features/dashboard/ImpactMetricsCard';
import { UpcomingEventsCard } from '@/components/features/dashboard/UpcomingEventsCard';
import { PrayerTimesCard } from '@/components/features/dashboard/PrayerTimesCard';
import { useDashboardCommunity } from '@/hooks/useDashboard';
import companyLogo from '@/assets/images/10001_logo.png';

export function HomePage() {
  const navigate = useNavigate();
  const { company } = useCompany();
  const { isAuthenticated, loginWithRedirect } = useAuth();
  const community = useDashboardCommunity();
  const showHeroSection = true;
  const showHeroSectionSimplified = false;

  if (isAuthenticated) {
    // Authenticated user - show comprehensive dashboard
    return <DonorDashboard />;
  }

  // Non-authenticated user landing page
  return (
    <div className="space-y-12">
      {/* Hero Section */}
      {showHeroSection && (
        <div className="text-center space-y-2 py-2">
          {company?.companyLogo && (
            <div className="flex justify-center mb-3">
              <img
                src={companyLogo}
                alt={company.companyName}
                className="h-20 w-20 rounded-b-lg object-contain shadow-sm border border-gray-100"
              />
            </div>
          )}
          <h3 className="text-2xl font-bold text-gray-900">Thank you for your generosity!</h3>
          <p className="text-lg text-gray-700 max-w-4xl mx-auto px-4">
            Welcome to {company?.companyName || 'our organization'} Donation Portal. Your donations
            will help us serve our community and support those in need.
          </p>
        </div>
      )}

      {showHeroSectionSimplified && (
        <div className="text-center space-y-4 py-8">
          <p className="text-2xl font-bold text-gray-900">
            Your donations will help us serve our community and support those in need.
          </p>
        </div>
      )}

      {/* Main Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto px-4">
        {/* Donate Card */}
        <button
          onClick={() => navigate('/donate')}
          className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
        >
          <div className="bg-linear-to-br from-pink-100 via-purple-100 to-blue-100 p-8 h-80 flex flex-col items-center justify-center space-y-6">
            <div className="bg-white rounded-full p-6 shadow-lg group-hover:shadow-xl transition-shadow">
              <HeartIcon className="h-20 w-20 text-primary-600" />
            </div>
            <div className="text-center space-y-2">
              <h2 className="text-3xl font-bold text-primary-900">MAKE A DIFFERENCE.</h2>
              <p className="text-2xl font-bold text-primary-600">DONATE TODAY.</p>
            </div>
          </div>
        </button>

        {/* Pledge Card */}
        <button
          onClick={() => navigate('/pledge')}
          className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
        >
          <div className="bg-linear-to-br from-emerald-100 via-teal-100 to-green-100 p-8 h-80 flex flex-col items-center justify-center space-y-6">
            <div className="bg-white rounded-full p-6 shadow-lg group-hover:shadow-xl transition-shadow">
              <HandRaisedIcon className="h-20 w-20 text-emerald-600" />
            </div>
            <div className="text-center space-y-2">
              <h2 className="text-3xl font-bold text-emerald-900">MAKE A</h2>
              <p className="text-2xl font-bold text-emerald-600">PLEDGE</p>
            </div>
          </div>
        </button>

        {/* Pay for Services Card */}
        <button
          onClick={() => navigate('/pay-for-service')}
          className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
        >
          <div className="bg-linear-to-br from-blue-100 via-cyan-100 to-teal-100 p-8 h-80 flex flex-col items-center justify-center space-y-6">
            <div className="bg-white rounded-full p-6 shadow-lg group-hover:shadow-xl transition-shadow">
              <CreditCardIcon className="h-20 w-20 text-blue-600" />
            </div>
            <div className="text-center space-y-2">
              <h2 className="text-3xl font-bold text-blue-900">Pay for</h2>
              <p className="text-2xl font-bold text-blue-600">Services</p>
            </div>
          </div>
        </button>
      </div>

      {/* Community Impact & Upcoming Events */}
      {community.data && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <ImpactMetricsCard />
          <PrayerTimesCard />
          <UpcomingEventsCard />
        </div>
      )}

      {/* Authentication Benefits Section */}
      <div className="max-w-6xl mx-auto px-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 space-y-6">
          <div className="text-center space-y-2">
            <h2 className="text-3xl font-bold text-gray-900">
              Create an Account for Enhanced Experience
            </h2>
            <p className="text-gray-600">
              Register or sign in to unlock additional features and benefits
            </p>
          </div>

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-6">
            {/* Donation History */}
            <div className="flex items-start space-x-4 p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
              <div className="shrink-0">
                <div className="bg-primary-100 rounded-lg p-3">
                  <ClockIcon className="h-6 w-6 text-primary-600" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Donation History</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Track all your donations and download receipts anytime
                </p>
              </div>
            </div>

            {/* Saved Payment Methods */}
            <div className="flex items-start space-x-4 p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
              <div className="shrink-0">
                <div className="bg-green-100 rounded-lg p-3">
                  <ShieldCheckIcon className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Secure Payments</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Save payment methods for quick and secure future donations
                </p>
              </div>
            </div>

            {/* Recurring Donations */}
            <div className="flex items-start space-x-4 p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
              <div className="shrink-0">
                <div className="bg-blue-100 rounded-lg p-3">
                  <BellIcon className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Manage Recurring</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Set up and manage monthly recurring donations easily
                </p>
              </div>
            </div>

            {/* Tax Receipts */}
            <div className="flex items-start space-x-4 p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
              <div className="shrink-0">
                <div className="bg-purple-100 rounded-lg p-3">
                  <svg
                    className="h-6 w-6 text-purple-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Tax Receipts</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Automatically generate and access tax-deductible receipts
                </p>
              </div>
            </div>

            {/* Quick Donate */}
            <div className="flex items-start space-x-4 p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
              <div className="shrink-0">
                <div className="bg-orange-100 rounded-lg p-3">
                  <HeartIcon className="h-6 w-6 text-orange-600" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Quick Donate</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Donate with one click using your saved preferences
                </p>
              </div>
            </div>

            {/* Notifications */}
            <div className="flex items-start space-x-4 p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
              <div className="shrink-0">
                <div className="bg-red-100 rounded-lg p-3">
                  <BellIcon className="h-6 w-6 text-red-600" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Stay Updated</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Receive updates about your donations and community impact
                </p>
              </div>
            </div>
          </div>

          {/* Sign Up CTA */}
          <div className="pt-6 text-center space-y-4">
            <button
              onClick={() => loginWithRedirect()}
              className="px-8 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors inline-flex items-center gap-2 shadow-md hover:shadow-lg"
            >
              <UserPlusIcon className="h-5 w-5" />
              Sign Up / Login
            </button>
            <p className="text-sm text-gray-600">
              Create an account or sign in to access these features
            </p>
          </div>
        </div>
      </div>

      {/* Guest Access Notice */}
      <div className="max-w-4xl mx-auto px-4 pb-8">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
          <p className="text-blue-900 font-medium">
            You can still donate or pay for services without an account!
          </p>
          <p className="text-blue-700 text-sm mt-2">
            Simply use the buttons above to get started. Registration is optional but recommended
            for frequent donors.
          </p>
        </div>
      </div>
    </div>
  );
}
